import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { setSession, clearAppCache } from "@/lib/api";
import { BrandMark } from "@/lib/branding";
import { ThemeToggle } from "@/components/ThemeToggle";

// Command-center login — Linear/Vercel light aesthetic. Left rail as a
// subtle brand statement (mono grid) and the auth form as the sole
// interactive object on the right. Everything else is deliberately quiet
// to keep the eye on the two inputs and the primary CTA.
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // 06/08/2026 — Ler o deep-link ANTES do clearAppCache (que faz
    // sessionStorage.clear()). Caso contrário perdemos a rota
    // original para onde o utilizador queria ir.
    let pending = null;
    try { pending = window.sessionStorage.getItem("caring:pendingRedirect"); } catch (_) {}
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.token, data.user);
      // 22/02/2026 — sempre que o utilizador abre nova sessão, limpa
      // caches locais (Cache API, Service Workers, IndexedDB, storages
      // voláteis) para garantir que dados stale não persistem entre
      // sessões / entre logins de utilizadores diferentes no mesmo
      // browser.
      await clearAppCache();
      toast.success(`Bem-vindo, ${data.user.name}`);
      // 06/08/2026 — Se o utilizador veio de um deep-link (ex.: email
      // do pontual → /leads/{id}?focus=pontual-price), envia-o para
      // a rota original em vez de /dashboard.
      const target = (pending && pending.startsWith("/") && !pending.startsWith("//"))
        ? pending
        : "/dashboard";
      navigate(target);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar sessão");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="login-screen">
      {/* Floating theme toggle so users can pick light/dark before signing in */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>
      {/* Left rail — mono grid, brand statement. Hidden on small screens. */}
      <aside className="hidden lg:flex w-[46%] xl:w-[52%] relative overflow-hidden border-r border-border bg-muted/40">
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            backgroundImage:
              "linear-gradient(to right, hsl(var(--foreground) / 0.04) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--foreground) / 0.04) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* Ambient accent — warm gold wash */}
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-primary/[0.14] blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-secondary/[0.10] blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between p-14 xl:p-16 w-full">
          <BrandMark size={40} />
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-medium mb-4 font-heading">
              Caring · Plataforma de gestão de clientes
            </div>
            <h2 className="font-heading text-4xl xl:text-5xl leading-[1.1] tracking-tight text-foreground max-w-md">
              Do primeiro contacto ao contrato assinado, num <span className="shift-hero-gradient">único cockpit</span>.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground max-w-md">
              Qualificação, estimativa, reunião técnica, proposta e Zoho — tudo com SLA e performance em tempo real.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-heading">
            <div>
              <div className="text-foreground font-heading text-2xl tracking-tight numeric">15m</div>
              <div className="mt-1">SLA primeiro contacto</div>
            </div>
            <div>
              <div className="text-foreground font-heading text-2xl tracking-tight numeric">2 000+</div>
              <div className="mt-1">Leads ativos</div>
            </div>
            <div>
              <div className="text-foreground font-heading text-2xl tracking-tight numeric">8 fases</div>
              <div className="mt-1">Ciclo do cliente</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Auth panel */}
      <main className="flex-1 flex items-center justify-center px-6 lg:px-10">
        <div className="w-full max-w-[380px]">
          {/* Mobile brand (visible only < lg) */}
          <div className="lg:hidden mb-10 flex items-center justify-center">
            <BrandMark size={40} />
          </div>

          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-medium font-heading mb-2">
              Bem-vindo de volta
            </div>
            <h1 className="font-heading text-3xl tracking-tight text-foreground font-bold">Iniciar sessão</h1>
            <p className="mt-2 text-[14px] text-muted-foreground">
              Aceda à plataforma de gestão de clientes e propostas.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-1.5 font-heading">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.6} />
                <input
                  id="login-email"
                  data-testid="login-email"
                  type="email"
                  autoComplete="username"
                  placeholder="nome@caring.pt"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full h-11 pl-10 pr-3 bg-card border border-input rounded-lg text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary transition-shadow"
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="block text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-1.5 font-heading">
                Palavra-passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.6} />
                <input
                  id="login-password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-11 pl-10 pr-10 bg-card border border-input rounded-lg text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary transition-shadow"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-password-toggle"
                  aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="mt-2 group w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed text-primary-foreground font-semibold rounded-xl h-11 text-[14px] shadow-sm hover:shadow-md transition-[background-color,box-shadow] font-heading tracking-tight"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Entrar
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 text-[11px] text-muted-foreground flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-emerald-500" />
            Sistema operacional · Caring Apoio Domiciliário
          </div>
        </div>
      </main>
    </div>
  );
}
